import { SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Col, Input, Row, Space } from 'antd';
import { useEffect, useState } from 'react';
import { getUsers } from '../../api';
import { components, paths } from '../../api/schema';
import DataTable, {
  ColumnsType,
  DataTableFilterCallback,
  DataTableOrderCallback,
  serializeFilters,
  serializeOrders,
} from '../../shared/dataTable/DataTable';
import useDebounce from '../../shared/hooks/useDebounce';
import PanelSkeleton from '../../shared/skeletons/PanelSkeleton';
import CreateUser from './CreateUser';
import UserDetail from './UserDetail';
import { DeleteUser, EditUser, ResetPassword } from './userPanelActions';

const usersTableColumns: ColumnsType<components['schemas']['UserCondensed']> = [
  {
    title: 'Nombre',
    dataIndex: 'firstName',
    key: 'firstName',
    columnType: 'text',
  },
  {
    title: 'Apellidos',
    dataIndex: 'lastName',
    key: 'lastName',
    columnType: 'text',
  },
  {
    title: 'Correo',
    dataIndex: 'email',
    key: 'email',
    columnType: 'text',
  },
  {
    title: 'Area',
    dataIndex: 'area',
    key: 'area',
    columnType: 'text',
  },
  {
    title: 'Rol',
    dataIndex: 'role',
    key: 'role',
    columnType: 'text',
  },
  {
    title: 'Zonas',
    dataIndex: 'zones',
    key: 'zones',
    columnType: 'text',
    hideFilter: true,
  },
];
const PAGE_SIZE = 50;

export default function UserPanel() {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>();
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce<string>(search, 300);
  const [params, setParams] = useState<paths['/user/list']['get']['parameters']['query']>({
    search: undefined,
    limit: PAGE_SIZE,
    page: 1,
    showDisabled: false,
    order: JSON.stringify([]),
    filter: JSON.stringify([]),
  });
  const { isLoading, isError, data, isFetching } = useQuery({
    queryKey: ['getUsers', params],
    queryFn: () => getUsers(params),
  });

  useEffect(() => {
    setParams((state) => ({ ...state, search: debouncedSearch }));
  }, [debouncedSearch]);

  const handleFiltering = (filter: DataTableFilterCallback) => {
    setParams((state) => ({ ...state, filter: serializeFilters(params.filter, filter) }));
  };

  const handleOrdering = (order: DataTableOrderCallback) => {
    setParams((state) => ({ ...state, order: serializeOrders(params.order, order) }));
  };

  const handlePageChange = (page: number) => {
    setParams((state) => ({ ...state, page }));
  };

  return (
    <Row>
      <Col span={24}>
        <Space size={'middle'}>
          <Button type="primary" onClick={() => setCreateModalOpen(true)}>
            Crear usuario
          </Button>
          <Input
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            suffix={<SearchOutlined />}
          />
        </Space>
      </Col>
      {isLoading || isError || isFetching ? (
        <PanelSkeleton />
      ) : (
        <>
          <Col span={24} style={{ marginTop: '10px' }}>
            <DataTable<components['schemas']['UserCondensed']>
              handleFilters={handleFiltering}
              handleOrder={handleOrdering}
              handlePageChange={handlePageChange}
              columns={usersTableColumns}
              results={data.results}
              activeFilters={params.filter}
              activeOrders={params.order}
              paginationData={{
                pageSize: PAGE_SIZE,
                count: data.count,
                currentPage: data.currentPage,
              }}
              actions={[EditUser, DeleteUser, ResetPassword]}
              onRowClick={(record) => {
                setSelectedUserId(record.id);
                setDetailModalOpen(true);
              }}
            />
          </Col>

          <UserDetail userId={selectedUserId} open={detailModalOpen} onCancel={() => setDetailModalOpen(false)} />
          <CreateUser
            open={createModalOpen}
            onCancel={() => setCreateModalOpen(false)}
            onCreate={() => setCreateModalOpen(false)}
          />
        </>
      )}
    </Row>
  );
}
